import {
  getEtatChargementMessage,
  getEtatChargementRawXml,
  listEtatChargementMessages,
  type EtatChargementDetail,
  type EtatChargementListItem,
  type EtatChargementListQuery,
} from '../api/etat-chargement';
import { StatusBadge } from '../components/StatusBadge';
import { CorrelationIdField, CustomsMessageConsole } from '../components/CustomsMessageConsole';
import { simpleValidationSections } from '../lib/customsMessages';

const PAGE_SIZE = 20;

const initialFilters: EtatChargementListQuery = {
  page: 1,
  pageSize: PAGE_SIZE,
  referenceEc: '',
  partnerCode: '',
  fonctionMessage: '',
  processingStatus: '',
};

export function EtatChargementPage() {
  return (
    <CustomsMessageConsole<EtatChargementListItem, EtatChargementListQuery, EtatChargementDetail>
      eyebrow="Maritime"
      title="Etat de Chargement (EC)"
      purpose="Inspect ADII loading-manifest (Etat de Chargement) XML messages, their normalized JSON, validation results and processing history. Read-only in this release; reprocess/replay actions are a planned follow-up."
      status="PARTIAL"
      next="Contact platform engineering to reprocess or replay a message until dedicated operator actions ship."
      listPanelTitle="Etat de Chargement messages"
      primaryColumnHeader="Reference EC"
      emptyRowLabel="No Etat de Chargement message matches the current filters."
      detailTitle="Etat de Chargement detail"
      initialFilters={initialFilters}
      pageSize={PAGE_SIZE}
      listFetch={listEtatChargementMessages}
      detailFetch={getEtatChargementMessage}
      rawXmlFetch={getEtatChargementRawXml}
      getRowId={(item) => item.id}
      getRowLabel={(item) => item.referenceEc ?? item.id}
      filterFields={(filters, patchFilters) => (
        <>
          <label className="filter-field">
            <span>Reference EC</span>
            <input value={filters.referenceEc ?? ''} onChange={(event) => patchFilters({ referenceEc: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Partner</span>
            <input value={filters.partnerCode ?? ''} onChange={(event) => patchFilters({ partnerCode: event.target.value })} />
          </label>
          <label className="filter-field">
            <span>Fonction message</span>
            <select value={filters.fonctionMessage ?? ''} onChange={(event) => patchFilters({ fonctionMessage: event.target.value })}>
              <option value="">All</option>
              <option value="1">1 — Création</option>
              <option value="2">2 — Modification</option>
              <option value="3">3 — Bon à embarquer</option>
              <option value="4">4 — Annulation</option>
            </select>
          </label>
          <label className="filter-field">
            <span>Processing status</span>
            <select value={filters.processingStatus ?? ''} onChange={(event) => patchFilters({ processingStatus: event.target.value })}>
              <option value="">All</option>
              <option value="PROCESSED">Processed</option>
              <option value="REPLAYED">Replayed</option>
              <option value="REJECTED">Rejected</option>
              <option value="QUARANTINED">Quarantined</option>
              <option value="FAILED">Failed</option>
            </select>
          </label>
        </>
      )}
      listColumns={[
        { header: 'Fonction', render: (item) => item.fonctionMessageLabel },
        { header: 'Partner', render: (item) => item.partnerCode ?? 'n/a' },
        { header: 'Bureau sortie', render: (item) => item.bureauSortie ?? 'n/a' },
        { header: 'Validation', render: (item) => <StatusBadge value={item.validationStatus} /> },
        { header: 'Processing', render: (item) => <StatusBadge value={item.processingStatus} /> },
        { header: 'Received', render: (item) => new Date(item.createdAt).toLocaleString('en-GB') },
      ]}
      detailSummaryFields={(data) => (
        <>
          <div><strong>Fonction message</strong><span>{data.fonctionMessageLabel}</span></div>
          <div><strong>Root element</strong><span>{data.rootElement}</span></div>
          <div><strong>Filename</strong><span className="mono">{data.filename ?? 'n/a'}</span></div>
          <div><strong>Partner</strong><span>{data.partnerCode ?? 'n/a'}</span></div>
          <div><strong>Reference EC</strong><span className="mono">{data.referenceEc ?? 'n/a'}</span></div>
          <div><strong>Version</strong><span>{data.numeroVersion ?? 'n/a'}</span></div>
          <div><strong>Date enregistrement</strong><span>{data.dateEnregistrementRaw ?? 'n/a'}</span></div>
          <div><strong>Date bon à embarquer</strong><span>{data.dateBonEmbarqueRaw ?? 'n/a'}</span></div>
          <div><strong>Bureau sortie</strong><span>{data.bureauSortie ?? 'n/a'}</span></div>
          <div><strong>Mode transport</strong><span>{data.modeTransportCode ?? 'n/a'}</span></div>
          <div><strong>Validation</strong><span><StatusBadge value={data.validationStatus} /></span></div>
          <div><strong>Processing</strong><span><StatusBadge value={data.processingStatus} /></span></div>
          <CorrelationIdField value={data.correlationId} />
        </>
      )}
      detailChildTables={(data) => (
        <>
          {data.children.transportMeans.length > 0 ? (
            <>
              <h3>Transport means</h3>
              <table className="data-table">
                <thead><tr><th>Pays</th><th>Type</th><th>Immatriculation</th><th>Tare</th></tr></thead>
                <tbody>
                  {data.children.transportMeans.map((item) => (
                    <tr key={item.id}>
                      <td>{item.pays ?? 'n/a'}</td>
                      <td>{item.typeMoyenCode ?? 'n/a'}</td>
                      <td className="mono">{item.immatriculation ?? 'n/a'}</td>
                      <td>{item.tare ?? 'n/a'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {data.children.declarations.length > 0 ? (
            <>
              <h3>Declarations</h3>
              <table className="data-table">
                <thead><tr><th>Reference DUM</th><th>Voyage</th><th>Annexe</th><th>Nature marchandise</th></tr></thead>
                <tbody>
                  {data.children.declarations.map((item) => (
                    <tr key={item.id}>
                      <td className="mono">{item.referenceDum ?? 'n/a'}</td>
                      <td>{item.numVoyage ?? 'n/a'}</td>
                      <td>{item.annexe ?? 'n/a'}</td>
                      <td>{item.natureMarchandise ?? 'n/a'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </>
      )}
      detailValidationReport={(data) => simpleValidationSections(data.validationReport)}
    />
  );
}
